/** Admin Dashboard SOP v1.0 — single source of truth for ops UI requirements. */

export const ADMIN_SOP_VERSION = "1.0 (Pre-UAT)";

export const ADMIN_ROLES = {
  SUPER_ADMIN: {
    id: "SUPER_ADMIN",
    label: "Super Admin",
    mapsFrom: ["ADMIN"],
    can: [
      "Access all modules",
      "Create users",
      "Suspend users",
      "Change permissions",
      "Access reports",
      "Configure platform settings",
    ],
    cannot: [],
  },
  OPERATIONS_MANAGER: {
    id: "OPERATIONS_MANAGER",
    label: "Operations Manager",
    mapsFrom: ["OPS"],
    can: [
      "Approve caregivers",
      "Manage customers",
      "View subscriptions",
      "Resolve tickets",
      "Access reports",
    ],
    cannot: ["Change system settings"],
  },
  VERIFICATION_EXECUTIVE: {
    id: "VERIFICATION_EXECUTIVE",
    label: "Verification Executive",
    can: [
      "Review caregiver documents",
      "Verify qualifications",
      "Request corrections",
    ],
    cannot: ["Approve payments", "Suspend users"],
  },
  CUSTOMER_SUPPORT: {
    id: "CUSTOMER_SUPPORT",
    label: "Customer Support Executive",
    can: ["Manage tickets", "View profiles", "Handle enquiries"],
    cannot: ["Modify verification status"],
  },
  TRUST_SAFETY: {
    id: "TRUST_SAFETY",
    label: "Trust & Safety Officer",
    can: [
      "Investigate complaints",
      "Suspend profiles",
      "Flag fraud",
      "Review incidents",
    ],
    cannot: [],
  },
};

export const ADMIN_MODULE_NAV = [
  { to: "/admin", label: "Dashboard Home", end: true, icon: "grid", permission: "dashboard" },
  { to: "/admin/bookings", label: "Booking Management", icon: "calendar", permission: "bookings" },
  { to: "/admin/shifts", label: "Shift Management", icon: "clock", permission: "bookings" },
  { to: "/admin/care-agreements", label: "Care Agreements", icon: "document", permission: "bookings" },
  { to: "/admin/matchmaking", label: "Matchmaking", icon: "match", permission: "matchmaking" },
  { to: "/admin/customers", label: "Customers", icon: "user", permission: "customers" },
  { to: "/admin/caregivers", label: "Caregivers", icon: "user", permission: "caregivers" },
  { to: "/admin/attendants", label: "Care Attendant", icon: "user", permission: "caregivers" },
  { to: "/admin/nurses", label: "Nurses", icon: "user", permission: "caregivers" },
  { to: "/admin/physiotherapists", label: "Physiotherapists", icon: "user", permission: "caregivers" },
  { to: "/admin/care-managers", label: "Care Manager", icon: "user", permission: "care_managers" },
  { to: "/admin/verification", label: "Verification Center", icon: "shield", permission: "verification" },
  { to: "/admin/police-verification", label: "Police Record Verification", icon: "shield", permission: "verification" },
  { to: "/admin/carehub", label: "CareHub Console", icon: "list", permission: "verification" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: "chart", permission: "subscriptions" },
  { to: "/admin/ncid", label: "NCID Management", icon: "list", permission: "npid" },
  { to: "/admin/complaints", label: "Complaints & Trust", icon: "alert", permission: "complaints" },
  { to: "/admin/emergency-requests", label: "Emergency Requests", icon: "alert", permission: "complaints" },
  { to: "/admin/tickets", label: "Support Ticket Management", icon: "help", permission: "complaints" },
  { to: "/admin/payments", label: "Payments & Finance", icon: "chart", permission: "payments" },
  { to: "/admin/trust-safety", label: "Trust & Safety", icon: "shield", permission: "trust_safety" },
  { to: "/admin/crms", label: "Caregiver Replacement", icon: "alert", permission: "trust_safety" },
  { to: "/admin/messenger", label: "Messenger", icon: "message", permission: "notifications" },
  { to: "/admin/notifications", label: "Notifications", icon: "message", permission: "notifications" },
  { to: "/admin/notification-templates", label: "Notification Templates", icon: "list", permission: "notifications" },
  { to: "/admin/reports", label: "Reports & Analytics", icon: "chart", permission: "reports" },
  { to: "/admin/lead-crm", label: "CRM Dashboard", icon: "chart", permission: "reports" },
  { to: "/admin/daily-ops", label: "Daily Operations", icon: "list", permission: "daily_ops" },
  { to: "/admin/roles", label: "User & Role Management", icon: "shield", permission: "admin_roles" },
  { to: "/admin/audit", label: "Audit Center", icon: "list", permission: "admin_roles" },
  { to: "/admin/integrations", label: "Integrations", icon: "grid", permission: "settings" },
  { to: "/admin/call-management", label: "Call Management", icon: "clock", permission: "settings" },
  { to: "/admin/features", label: "Feature Management", icon: "list", permission: "settings" },
  { to: "/admin/platform", label: "Platform Configuration", icon: "settings", permission: "settings" },
  { to: "/admin/settings", label: "System Settings", icon: "settings", permission: "settings" },
];

export const ROLE_MODULE_ACCESS = {
  SUPER_ADMIN: ADMIN_MODULE_NAV.map((m) => m.permission),
  OPERATIONS_MANAGER: [
    "dashboard", "bookings", "matchmaking", "customers", "caregivers", "care_managers", "verification",
    "subscriptions", "npid", "complaints", "payments", "notifications", "reports", "daily_ops",
  ],
  VERIFICATION_EXECUTIVE: ["dashboard", "verification", "caregivers", "npid", "daily_ops"],
  CUSTOMER_SUPPORT: ["dashboard", "bookings", "customers", "complaints", "notifications", "daily_ops"],
  TRUST_SAFETY: ["dashboard", "trust_safety", "complaints", "caregivers", "care_managers", "reports"],
};

export const DASHBOARD_SNAPSHOT_SECTIONS = [
  {
    id: "customers",
    title: "Customer Metrics",
    metrics: [
      { key: "newRegistrationsToday", label: "New Registrations Today" },
      { key: "activeSubscribers", label: "Active Subscribers" },
      { key: "expiringSubscriptions", label: "Expiring Subscriptions" },
    ],
  },
  {
    id: "caregivers",
    title: "Caregiver Metrics",
    metrics: [
      { key: "newCaregiverRegistrations", label: "New Registrations" },
      { key: "pendingVerification", label: "Pending Verification" },
      { key: "approvedProfiles", label: "Approved Profiles" },
    ],
  },
  {
    id: "marketplace",
    title: "Marketplace Metrics",
    metrics: [
      { key: "searches", label: "Searches" },
      { key: "profileViews", label: "Profile Views" },
      { key: "contactRequests", label: "Contact Requests" },
      { key: "successfulHires", label: "Successful Hires" },
    ],
  },
  {
    id: "revenue",
    title: "Revenue Metrics",
    metrics: [
      { key: "subscriptionRevenue", label: "Subscription Revenue" },
      { key: "renewalRevenue", label: "Renewal Revenue" },
      { key: "pendingPayments", label: "Pending Payments" },
    ],
  },
  {
    id: "trust",
    title: "Trust Metrics",
    metrics: [
      { key: "complaints", label: "Complaints" },
      { key: "suspensions", label: "Suspensions" },
      { key: "fraudAlerts", label: "Fraud Alerts" },
    ],
  },
];

export const CUSTOMER_LIST_COLUMNS = [
  { key: "id", label: "Customer ID" },
  { key: "name", label: "Name" },
  { key: "mobile", label: "Mobile" },
  { key: "city", label: "City" },
  { key: "leadSource", label: "How they heard about us" },
  { key: "subscriptionStatus", label: "Subscription Status" },
  { key: "registeredAt", label: "Registration Date" },
];

export const CUSTOMER_ACTIONS = [
  "Open profile",
  "Edit Profile",
  "Suspend Account",
  "Reactivate Account",
  "Reset Password",
  "Generate Member Card",
  "Download Member Card",
];

export const CUSTOMER_PROFILE_SECTIONS = [
  "Personal Details", "Elderly Profile", "Subscription Details", "Search History", "Contact Requests", "Complaints Raised",
];

export const CAREGIVER_LIST_COLUMNS = [
  { key: "npid", label: "NCID" },
  { key: "name", label: "Name" },
  { key: "serviceType", label: "Service Type" },
  { key: "selectedShiftLabel", label: "Selected shifts" },
  { key: "profileFlag", label: "Profile flag" },
  { key: "verificationStatus", label: "Verification Status" },
  { key: "npidScore", label: "NCID Score" },
  { key: "profileStatus", label: "Profile Status" },
  { key: "engagementStatus", label: "Engagement Status" },
];

export const CAREGIVER_FILTERS = ["Service Type", "City", "Verification Status", "Gender", "Shift Type", "Availability", "Experience"];

export const CAREGIVER_ACTIONS = [
  "View Profile", "Approve", "Reject", "Suspend", "Deactivate", "Edit Profile", "Generate Verification Report",
];

export const VERIFICATION_QUEUE_TABS = [
  { id: "PENDING", label: "Pending Applications" },
  { id: "IN_REVIEW", label: "In Review" },
  { id: "CORRECTIONS", label: "Corrections Requested" },
  { id: "APPROVED", label: "Approved" },
  { id: "REJECTED", label: "Rejected" },
];

export const VERIFICATION_WORKFLOW_STEPS = [
  "Open Application",
  "Verify Documents",
  "Verify Qualifications",
  "Verify Experience",
  "Verify References",
  "Generate NCID",
  "Approve",
];

export const DOCUMENT_CHECKLISTS = {
  identity: {
    title: "Identity Verification",
    items: ["Aadhaar", "Photograph", "Mobile Verification", "Address Proof"],
  },
  nurse: {
    title: "Nurse Verification",
    items: ["Nursing Qualification", "Registration Number", "Clinical Experience"],
  },
  physio: {
    title: "Physiotherapist Verification",
    items: ["Degree Certificate", "Registration Number", "Experience Proof"],
  },
  attendant: {
    title: "Attendant Verification",
    items: ["Experience Declaration", "Reference Check"],
  },
};

export const NCID_SCORE_COMPONENTS = [
  { component: "Verification completeness", weight: "25%" },
  { component: "Clinical experience log", weight: "25%" },
  { component: "Ratings (min. 3)", weight: "20%" },
  { component: "Career Ladder level", weight: "15%" },
  { component: "Certificates", weight: "10%" },
  { component: "Complaint-free tenure", weight: "5%" },
];

export const NPID_SCORE_COMPONENTS = NCID_SCORE_COMPONENTS;

export const NCID_ADMIN_ACTIONS = [
  "Generate NCID",
  "Generate NCID Card",
  "Share NCID Card",
  "View Card Generation History",
  "Sync Profile Data",
  "Activate NCID",
  "Recalculate Score",
  "Lock Score",
  "Flag Score",
  "Review Score History",
  "View Activation Checklist",
];

export const NPID_ADMIN_ACTIONS = NCID_ADMIN_ACTIONS;

export const SUBSCRIPTION_COLUMNS = [
  { key: "subscriptionId", label: "Subscription ID" },
  { key: "memberId", label: "Member ID" },
  { key: "patientId", label: "Patient ID" },
  { key: "patient", label: "Patient" },
  { key: "customer", label: "Account Name" },
  { key: "mobile", label: "Mobile" },
  { key: "plan", label: "Plan" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "paymentStatus", label: "Payment Status" },
];

export const SUBSCRIPTION_ACTIONS = [
  "Activate",
  "Suspend",
  "Renew",
  "Extend",
  "Cancel",
  "Refund",
  "Resend Invoice",
];

export const SUBSCRIPTION_ALERTS = ["Expiring in 30 Days", "Expiring in 7 Days", "Expired"];

export const PAYMENT_COLUMNS = [
  { key: "transactionId", label: "Transaction ID" },
  { key: "customer", label: "Customer" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
  { key: "date", label: "Date" },
];

export const PAYMENT_ACTIONS = ["Verify Payment", "Refund", "Investigate Failure", "Export Report"];

export const COMPLAINT_COLUMNS = [
  { key: "ticketNumber", label: "Ticket Number" },
  { key: "raisedBy", label: "Raised By" },
  { key: "category", label: "Category" },
  { key: "severity", label: "Severity" },
  { key: "status", label: "Status" },
];

export const COMPLAINT_CATEGORIES = {
  customer: [
    "Caregiver Misconduct", "Fake Information", "Poor Behaviour", "No Response",
  ],
  caregiver: [
    "Harassment", "Fake Reviews", "Customer Abuse", "Technical Issues",
  ],
  careManager: {
    customer: ["Lack of Follow-Up", "Poor Communication", "Negligence", "Non-Availability"],
    caregiver: ["Harassment", "Biased Behaviour", "Delayed Escalation", "Misconduct"],
  },
};

export const TICKET_SLA = [
  { priority: "Low", resolutionTime: "72 Hours" },
  { priority: "Medium", resolutionTime: "48 Hours" },
  { priority: "High", resolutionTime: "24 Hours" },
  { priority: "Critical", resolutionTime: "Immediate" },
];

export const TICKET_WORKFLOW = [
  "Complaint Raised", "Ticket Created", "Assigned", "Investigation", "Resolution", "Closure",
];

export const TRUST_FRAUD_TRIGGERS = [
  "Duplicate Profiles", "Fake Documents", "Excessive Complaints", "Identity Mismatch",
];

export const TRUST_RISK_LEVELS = [
  { level: "Green", meaning: "Normal" },
  { level: "Yellow", meaning: "Under Review" },
  { level: "Orange", meaning: "Investigation" },
  { level: "Red", meaning: "Suspended" },
];

export const TRUST_ACTIONS = ["Flag User", "Suspend Profile", "Launch Investigation", "Blacklist User"];

export const NOTIFICATION_CHANNELS = ["WhatsApp", "Email", "SMS"];

export const NOTIFICATION_TEMPLATES = [
  "Customer Registration",
  "Caregiver Registration",
  "Verification Approved",
  "Verification Rejected",
  "Subscription Renewal",
  "Complaint Updates",
  "Profile Suspension",
  "Admin Login — Super Admin",
  "Admin Login — Operations Manager",
  "Admin Login — Verification Executive",
  "Admin Login — Customer Support Executive",
  "Admin Login — Trust & Safety Officer",
];

export const REPORT_TYPES = {
  daily: ["Registrations", "Verifications", "Subscriptions", "Revenue"],
  weekly: ["Conversion Rate", "Hiring Rate", "Complaints", "NCID Changes"],
  monthly: ["Marketplace Growth", "Revenue", "Retention", "Churn", "Quality Metrics"],
};

export const DAILY_CHECKLIST = {
  verification: [
    "Review pending profiles", "Process approvals", "Request corrections",
  ],
  support: [
    "Review tickets", "Respond to enquiries", "Escalate critical issues",
  ],
  operations: [
    "Review dashboard", "Check revenue", "Check subscriber growth", "Review complaints",
  ],
};

export const PRE_LAUNCH_KPIS = {
  verification: ["Verification TAT", "Approval Rate", "Rejection Rate"],
  marketplace: ["Active Subscribers", "Contact Requests", "Successful Matches"],
  quality: ["Complaint Rate", "Fraud Rate", "NCID Average Score"],
  revenue: ["New Subscriptions", "Renewals", "Churn Rate"],
};

export const NEMA_ADVANCED_REPORTS = [
  { id: "caregiverHeatMap", title: "Caregiver Heat Map", desc: "Supply availability by area" },
  { id: "demandHeatMap", title: "Demand Heat Map", desc: "Customer demand by area" },
  { id: "skillGap", title: "Skill Gap Report", desc: "Demand > Supply skills (ICU Nurses, Dementia Care, Neuro Physios)" },
  { id: "npidDistribution", title: "NCID Distribution Report", desc: "Score-wise caregiver segmentation" },
  { id: "conversionFunnel", title: "Match Conversion Funnel", desc: "Search → Profile View → Contact → Conversation → Hiring" },
];

export const CARE_MANAGER_COLUMNS = [
  { key: "cmid", label: "CMID" },
  { key: "name", label: "Name" },
  { key: "mobile", label: "Mobile" },
  { key: "city", label: "City" },
  { key: "verificationStatus", label: "Verification Status" },
  { key: "npidScore", label: "NCID Score" },
  { key: "activeClients", label: "Active Clients" },
  { key: "activeCaregivers", label: "Active Caregivers Managed" },
  { key: "subscriptionStatus", label: "Subscription Status" },
  { key: "profileStatus", label: "Profile Status" },
];

export const CARE_MANAGER_FILTERS = [
  "City", "Experience", "Verification Status", "NCID Range", "Active Clients", "Subscription Status", "Specialization",
];

export const CARE_MANAGER_PROFILE_SECTIONS = [
  "Personal Details", "Professional Information", "Marketplace Information", "Performance Metrics",
];

export const CARE_MANAGER_VERIFICATION_STEPS = [
  "Application Submitted", "Document Review", "Identity Verification", "Qualification Verification",
  "Background Check", "Reference Verification", "NCID Generation", "Approval",
];

export const CARE_MANAGER_DOC_CHECKLIST = {
  identity: ["Aadhaar", "PAN Card", "Photograph", "Mobile Verification", "Address Proof"],
  professional: ["Qualification Certificates", "Experience Proof", "Training Certificates", "Reference Letters"],
};

export const CARE_MANAGER_SCORECARD = [
  { kpi: "Customer Satisfaction", weight: "25%" },
  { kpi: "Caregiver Satisfaction", weight: "15%" },
  { kpi: "Complaint Rate", weight: "20%" },
  { kpi: "Documentation Compliance", weight: "10%" },
  { kpi: "Visit Compliance", weight: "15%" },
  { kpi: "Retention Rate", weight: "15%" },
];

export const CARE_MANAGER_ADVANCED = [
  "Care Manager Heat Map", "Client Load Monitoring", "Service Quality Dashboard", "Care Manager Leaderboard",
];

export const LEGACY_ADMIN_REDIRECTS = {
  "/admin/npid": "/admin/ncid",
  "/admin/people": "/admin/customers",
  "/admin/customer-kyc": "/admin/verification?tab=customers",
  "/admin/care-manager-documents": "/admin/care-managers?tab=verification",
  "/admin/revenue": "/admin/payments",
  "/admin/security": "/admin/trust-safety",
  "/admin/exceptions": "/admin/complaints",
  "/admin/platform": "/admin/settings",
  "/admin/city-liquidity": "/admin/reports?tab=advanced",
  "/admin/premium-qualification": "/admin/caregivers",
  "/admin/caregiver-compliance": "/admin/caregivers",
  "/admin/insurance-workflow": "/admin/settings",
  "/admin/intake-assessments": "/admin/verification",
  "/admin/hourly-pricing": "/admin/settings",
  "/admin/task-master": "/admin/settings",
};
